// Página de listagem de transportadoras
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Loading } from '../../../components/common/Loading';
import { Plus, Edit, Trash2, AlertCircle } from 'lucide-react';
import { getTransportadoras, createTransportadora, updateTransportadora, deleteTransportadora } from '../../../services/transportadorasService';
import { useEmpresa } from '../../../contexts/EmpresaContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { formatCNPJ, formatDate } from '../../../utils/formatters';
import type { Transportadora } from '../../../types';

export function Transportadora() {
  const { empresaSelecionada, contaId } = useEmpresa();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [busca, setBusca] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [transportadoraSelecionada, setTransportadoraSelecionada] = useState<Transportadora | null>(null);
  const [transportadoras, setTransportadoras] = useState<Transportadora[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (empresaSelecionada) {
      carregarTransportadoras();
    }
  }, [empresaSelecionada]);

  const carregarTransportadoras = async () => {
    if (!empresaSelecionada) return;
    
    try {
      setLoading(true);
      setError(null);
      const dados = await getTransportadoras(empresaSelecionada.id);
      setTransportadoras(dados);
    } catch (err: any) {
      setError(err.message || 'Erro ao carregar transportadoras');
      console.error('Erro ao carregar transportadoras:', err);
    } finally {
      setLoading(false);
    }
  };

  const transportadorasFiltradas = transportadoras.filter(t =>
    t.nome.toLowerCase().includes(busca.toLowerCase()) ||
    t.cnpj.includes(busca) ||
    t.email.toLowerCase().includes(busca.toLowerCase())
  );

  const abrirModal = (transportadora?: Transportadora) => {
    setTransportadoraSelecionada(transportadora || null);
    setIsModalOpen(true);
  };

  const fecharModal = () => {
    setIsModalOpen(false);
    setTransportadoraSelecionada(null);
  };

  const handleDelete = async (id: string) => {
    if (!canDelete) {
      alert('Você não tem permissão para excluir transportadoras');
      return;
    }

    if (!confirm('Tem certeza que deseja excluir esta transportadora?')) {
      return;
    }

    try {
      await deleteTransportadora(id);
      await carregarTransportadoras();
    } catch (err: any) {
      alert(err.message || 'Erro ao excluir transportadora');
      console.error('Erro ao excluir transportadora:', err);
    }
  };

  if (!empresaSelecionada) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Transportadora' }]} />
        <Card>
          <div className="text-center py-12">
            <p className="text-gray-600 dark:text-gray-400">Selecione uma empresa para visualizar as transportadoras</p>
          </div>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Transportadora' }]} />
        <div className="flex items-center justify-center py-12">
          <Loading />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Cadastros', path: '/app/cadastros' }, { label: 'Transportadora' }]} />
      
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Transportadoras</h1>
        <Button onClick={() => abrirModal()} variant="primary">
          <Plus className="w-4 h-4 mr-2" />
          Nova Transportadora
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}
      
      <Card>
        <Input
          label="Buscar Transportadora"
          placeholder="Nome, CNPJ ou email"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
      </Card>
      
      <Card>
        <Table
          data={transportadorasFiltradas}
          columns={[
            { key: 'nome', label: 'Nome' },
            { key: 'cnpj', label: 'CNPJ', render: (item) => formatCNPJ(item.cnpj) },
            { key: 'email', label: 'Email' },
            { key: 'telefone', label: 'Telefone' },
            { key: 'createdAt', label: 'Cadastrado em', render: (item) => formatDate(item.createdAt) },
            {
              key: 'actions',
              label: 'Ações',
              render: (item) => (
                <div className="flex items-center gap-2">
                  {canUpdate && (
                    <button onClick={() => abrirModal(item)} className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg">
                      <Edit className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => handleDelete(item.id)} className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>
      
      <Modal isOpen={isModalOpen} onClose={fecharModal} title={transportadoraSelecionada ? 'Editar Transportadora' : 'Nova Transportadora'} size="lg">
        <TransportadoraForm 
          transportadora={transportadoraSelecionada} 
          onClose={fecharModal}
          onSuccess={carregarTransportadoras}
          isSaving={isSaving}
          setIsSaving={setIsSaving}
        />
      </Modal>
    </div>
  );
}

function TransportadoraForm({ 
  transportadora, 
  onClose, 
  onSuccess,
  isSaving,
  setIsSaving
}: { 
  transportadora: Transportadora | null; 
  onClose: () => void;
  onSuccess: () => void;
  isSaving: boolean;
  setIsSaving: (value: boolean) => void;
}) {
  const { empresaSelecionada, contaId } = useEmpresa();
  const [formData, setFormData] = useState({
    nome: transportadora?.nome || '',
    cnpj: transportadora?.cnpj || '',
    email: transportadora?.email || '',
    telefone: transportadora?.telefone || '',
    cep: transportadora?.endereco.cep || '',
    logradouro: transportadora?.endereco.logradouro || '',
    numero: transportadora?.endereco.numero || '',
    complemento: transportadora?.endereco.complemento || '',
    bairro: transportadora?.endereco.bairro || '',
    cidade: transportadora?.endereco.cidade || '',
    estado: transportadora?.endereco.estado || '',
  });
  const [error, setError] = useState<string | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!empresaSelecionada || !contaId) {
      setError('Empresa não selecionada ou sem conta vinculada');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      const transportadoraData = {
        nome: formData.nome,
        cnpj: formData.cnpj,
        email: formData.email,
        telefone: formData.telefone,
        endereco: {
          cep: formData.cep,
          logradouro: formData.logradouro,
          numero: formData.numero,
          complemento: formData.complemento || undefined,
          bairro: formData.bairro,
          cidade: formData.cidade,
          estado: formData.estado,
        },
      };

      if (transportadora) {
        await updateTransportadora(transportadora.id, transportadoraData);
      } else {
        await createTransportadora(transportadoraData, contaId, empresaSelecionada.id);
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.message || 'Erro ao salvar transportadora');
      console.error('Erro ao salvar transportadora:', err);
    } finally {
      setIsSaving(false);
    }
  };
  
  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nome"
          value={formData.nome}
          onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="CNPJ"
          value={formData.cnpj}
          onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Email"
          type="email"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Telefone"
          value={formData.telefone}
          onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="CEP"
          value={formData.cep}
          onChange={(e) => setFormData({ ...formData, cep: e.target.value })}
          required
          disabled={isSaving}
        />
        <div className="md:col-span-2">
          <Input
            label="Logradouro"
            value={formData.logradouro}
            onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
            required
            disabled={isSaving}
          />
        </div>
        <Input
          label="Número"
          value={formData.numero}
          onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Complemento"
          value={formData.complemento}
          onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
          disabled={isSaving}
        />
        <Input
          label="Bairro"
          value={formData.bairro}
          onChange={(e) => setFormData({ ...formData, bairro: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Cidade"
          value={formData.cidade}
          onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
          required
          disabled={isSaving}
        />
        <Input
          label="Estado"
          value={formData.estado}
          onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
          required
          disabled={isSaving}
        />
      </div>
      
      <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="secondary" onClick={onClose} disabled={isSaving}>
          Cancelar
        </Button>
        <Button type="submit" variant="primary" disabled={isSaving} isLoading={isSaving}>
          {isSaving ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
